import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';

import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import MicroModal from 'micromodal';
import { template } from 'lodash';
import { select, Store } from '@ngxs/store';
import { I18nPluralPipe } from '@angular/common';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { takeUntil } from 'rxjs/operators';
import { HeaderSearchExecutionService } from '../../services/header-search-execution/header-search-execution.service';
import {
  ConfigurationState,
  InventoryManagementState,
  LaunchDarklyService
} from 'remarketing-angular-library';

enum ActionSelection {
  Vin = 'VIN',
  Stock = 'STOCK'
}

@Component({
  selector: 'app-multi-vin-search-modal',
  templateUrl: './multi-vin-search-modal.component.html',
  styleUrl: './multi-vin-search-modal.component.scss',
  providers: [I18nPluralPipe],
  imports: [TranslocoModule, ReactiveFormsModule],
  standalone: true
  })
export class MultiVinSearchModalComponent extends SaComponent implements OnInit {
  readonly modalClosed = output<void>();
  readonly isLoading$ = input(true);

  private formBuilder = inject(UntypedFormBuilder);
  private _store = inject(Store);
  private _i18nPlural = inject(I18nPluralPipe);
  private readonly _analyticsService = inject(AnalyticsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly headerSearchExecutionService = inject(HeaderSearchExecutionService);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  readonly modalId = 'inventory-management-multi-vin-modal';
  multiVinForm: UntypedFormGroup;
  protected readonly inventorySearchCriteria$ = select(InventoryManagementState.searchCriteria);
  protected readonly configuration$ = select(ConfigurationState.configuration);
  maxLength = 9498;
  ActionSelection = ActionSelection;
  textCurrentActionType$ = signal('');

  translations = {
    dialogHeadlineMsg: '',
    textVin: '',
    textStockNumber: '',
    textSearchBy: '',
    textEnterFullLabelAction: '',
    textExceeded: '',
    btnClear: '',
    labelSearch: '',
    modalClose: ''
  };

  public instructionMessage$ = computed(() => {
    return this.translations.textEnterFullLabelAction.length > 0
      ? this.translocoService.translate('sellModule.manageInventory.multiVinModal.textEnterFullLabelAction', {
        labelAction: this.textCurrentActionType$()
      })
      : '';
  });

  constructor() {
    super();
    this.initializeMultiVinForm();
    effect(() => {
      if(this.isMultipleStockNumberSearch || this.isSingleStockSearch) {
        this.multiVinForm.get('action').setValue(ActionSelection.Stock);
        this.onRadioSelection(ActionSelection.Stock);
      } else {
        this.multiVinForm.get('action').setValue(ActionSelection.Vin);
        this.onRadioSelection(ActionSelection.Vin);
      }
    });
  }

  ngOnInit(): void {
    this.getTranslations();
    this.initializeClearFormOnActionChange();

    // Set initial selection based on current state
    const initialToggleSelection = (this.isMultipleStockNumberSearch || this.isSingleStockSearch)
      ? ActionSelection.Stock : ActionSelection.Vin;

    this.multiVinForm.get('action').setValue(initialToggleSelection);
    this.onRadioSelection(initialToggleSelection);
  }

  get isMultipleStockNumberSearch(): boolean {
    const searchCriteria = this.inventorySearchCriteria$();
    return searchCriteria?.vins?.length <= 1 && searchCriteria?.stockNumbers?.length > 1;
  }

  get isSingleStockSearch(): boolean {
    const searchCriteria = this.inventorySearchCriteria$();
    return !searchCriteria?.vins?.length && searchCriteria?.stockNumbers?.length === 1;
  }

  private validateEntriesLength = (control: AbstractControl) => {
    const maxEntriesLength = 500;
    const entries = this._transformToEntries(control.value);
    return entries.length > maxEntriesLength ? { difference: entries.length - maxEntriesLength } : null;
  };

  initializeMultiVinForm(): void {
    this.multiVinForm = this.formBuilder.group({
      action: [ActionSelection.Vin],
      entries: ['', this.validateEntriesLength]
    });
  }

  initializeClearFormOnActionChange(): void {
    this.controlAction.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.clearInput());
  }

  onTextAreaInput(): void {
    this.controlEntries.setValue(this.controlEntries.value.replace(/[^a-zA-Z0-9\s\,\r\n|\r|\n]/gi, ''));
  }

  onSubmit(): void {
    const count = this.entriesList.length;
    // Old flow had no handling logic for invalid count, need to get UX design for message to user
    if (count === 0 || count > 500) {
      return;
    } else {
      const vinsForPayload = ActionSelection.Vin === this.controlAction.value ? this.entriesList : [];
      const stockNumbersForPayload = ActionSelection.Stock === this.controlAction.value ? this.entriesList : [];
      const sellerLegacyIdForPayload = null;
      const isDesktop = true;
      this.headerSearchExecutionService.executeCommonSearchBarTextInputSearch(
        vinsForPayload,
        stockNumbersForPayload,
        sellerLegacyIdForPayload,
        isDesktop
      );
      this.trackAnalyticsAfterSearch();
      this.closeMicroModal();
    }
  }

  onRadioSelection(action: ActionSelection): void {
    const searchCriteria = this.inventorySearchCriteria$();
    switch (action) {
    case ActionSelection.Stock:
      this.textCurrentActionType$.set(this.translations.textStockNumber);
      this.controlEntries.setValue(searchCriteria?.stockNumbers?.length ? searchCriteria.stockNumbers : '');
      break;
    default:
      this.textCurrentActionType$.set(this.translations.textVin);
      this.controlEntries.setValue(searchCriteria?.vins?.length ? searchCriteria.vins : '');
      break;
    }
  }

  trackAnalyticsAfterSearch(): void {
    this._analyticsService.track('customEvent', {
      attr: {
        formValues: {
          name: 'Multi Vin Search',
          status: 'success',
          errors: null,
          type: '',
          step: '',
          stepname: '',
          items: [
            {
              allow: 'yes',
              error: null,
              name: 'Entries',
              track: 'yes',
              value: this.entriesList
            }
          ]
        }
      }
    });
  }

  closeMicroModal(): void {
    MicroModal.close(this.modalId);
  }

  clearInput(): void {
    this.controlEntries.setValue('');
  }

  getStockMvaReferenceForDataTrack(): string {
    return this.inServiceVehiclesEnabled ? 'MVA' : 'Stock/Unit #';
  }

  private _transformToEntries(value: string[] = []): string[] {
    let stringValue: string;

    try {
      stringValue = value.toString();
    } catch (error) {
      stringValue = '';
    }

    return stringValue
      .replace(/[\r\n|\r|\n]+/gi, ',')
      .replace(/[\s,]+/gi, ',')
      .split(',')
      .filter(entry => entry.length);
  }

  get dialogHeadline(): string {
    return this.translations.dialogHeadlineMsg;
  }

  get entriesList(): string[] {
    return this._transformToEntries(this.controlEntries.value);
  }

  get entriesErrorCount(): string {
    const { difference } = this.controlEntries.errors || {};
    const message = this._i18nPlural.transform(difference, {
      '=1': '# <%= action %>',
      other: '# <%= action %>s'
    });

    return template(message)({ action: this.labelAction });
  }

  get hasEntriesError(): boolean {
    return !!this.controlEntries.errors;
  }

  get isActionStock(): boolean {
    return this.controlAction.value === ActionSelection.Stock;
  }
  get isActionVin(): boolean {
    return this.controlAction.value === ActionSelection.Vin;
  }

  get labelAction(): string {
    if (this.isActionStock) return this.translations.textStockNumber;
    else if (this.isActionVin) return this.translations.textVin;
  }

  get controlAction(): AbstractControl {
    return this.multiVinForm.get('action');
  }

  get controlEntries(): AbstractControl {
    return this.multiVinForm.get('entries');
  }

  get inServiceVehiclesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get tenantName() {
    return this.configuration$()?.tenantName;
  }

  getTranslations(): void {
    const keyParams = {
      'sellModule.manageInventory.multiVinModal': {},
      common: {}
    };

    this.translocoService.selectTranslateObject(keyParams).subscribe(translate => {
      this.translations.dialogHeadlineMsg = translate[0].dialogHeadlineMsg;
      this.translations.textVin = translate[0].textVIN;
      this.translations.textStockNumber = translate[0].textStockUnit;
      this.translations.textSearchBy = translate[0].textSearchBy;
      this.translations.textEnterFullLabelAction = translate[0].textEnterFullLabelAction;
      this.translations.textExceeded = translate[0].textExceeded;
      this.translations.btnClear = translate[0].btnClear;
      this.translations.labelSearch = translate[1].labelSearch;
      this.translations.modalClose = translate[1].modalClose;
    });
  }
}
